import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Spinner } from "@chakra-ui/react";
import NuMultiSelect from "@/js/components/NuMultiSelect";
import { getUserDisplayTextForSelect, userDisplayText } from "@/js/utils/userUtils";
import { ClassroomDetails, ClassroomOverviewDTO, ClassroomTeacherDTO, UserDetail } from "@/stores/classroomStore_I";
import { UserInfo } from "@/js/api/authApi";
import { getUserDetail, searchUsers } from "@/js/api/usersApi";
import { useAddTeacherToClassroomMutation, useRemoveTeacherFromClassroomMutation } from "@/query/modifyTeachersQuery";
import { useToast } from "@/components/ui/Toast";
import { getClassroomDisplayName, isClassroomSynced } from "@/utils/classroomUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { colours } from "@/utils/themeUtils";
import { ClassroomType } from "@/types/classrooms";

export interface ModifyTeachersContentProps {
	classroom: ClassroomOverviewDTO;
	classroomDetails: ClassroomDetails | undefined;
	isTeachersLoaded: boolean;
}

export const ModifyTeachersContent: React.FC<ModifyTeachersContentProps> = ({ classroom, classroomDetails, isTeachersLoaded }) => {
	const { t } = useTranslation();
	const { successToast, errorToast } = useToast();

	const [teacherError, setTeacherError] = useState<string>();
	const [lastSelectedTeacher, setLastSelectedTeacher] = useState<UserDetail>();
	const [isSaving, setIsSaving] = useState(false);

	const teachers = classroomDetails?.teachers ?? [];
	const classroomDisplayName = getClassroomDisplayName(classroom);
	const isGroup = classroom.type.toLowerCase() === ClassroomType.Group;
	const isClassroomSyncedStatus = isClassroomSynced(classroom);

	const addTeacherMutation = useAddTeacherToClassroomMutation(classroom.id, classroom.stableIdV1);
	const removeTeacherMutation = useRemoveTeacherFromClassroomMutation(classroom.id, classroom.stableIdV1);

	const getUsersCb = async (filterStr: string): Promise<{ text: string; value: UserInfo }[]> => {
		if (filterStr) {
			const users = await searchUsers(filterStr);
			return users.map((user) => ({
				text: getUserDisplayTextForSelect(user),
				value: user,
			}));
		}
		return [];
	};

	const findTeacherBySelection = (selection: UserInfo | string): ClassroomTeacherDTO | undefined => {
		if (typeof selection === "string") {
			return teachers.find((teacher) => getUserDisplayTextForSelect(teacher as UserInfo) === selection);
		}
		return teachers.find((teacher) => teacher.username === selection.username);
	};

	const handleAddTeacher = async (user: UserInfo) => {
		if (teachers.some((previous) => user.username === previous.username)) {
			setTeacherError(t("classroomEdit.teacherAlreadyInClass", { name: getUserDisplayTextForSelect(user) }));
			return;
		}

		setIsSaving(true);
		try {
			const foundUser = await getUserDetail(user.username, false, false, true);
			setLastSelectedTeacher(foundUser);

			if (!foundUser) {
				setTeacherError(t("classroomEdit.notValidTeacher", { name: getUserDisplayTextForSelect(user) }));
				return;
			}

			if (!foundUser.isTeacher) {
				return;
			}

			await addTeacherMutation.mutateAsync(foundUser.qoriaUserId);

			successToast({
				isClosable: true,
				title: t("overview.settingsTeacherAddedSuccess", {
					user: userDisplayText(foundUser),
					classroom: classroomDisplayName,
				}),
			});
		} catch (error) {
			console.error("Failed to add teacher to class:", error);
			errorToast({
				title: t("overview.settingsTeacherAddError", {
					user: userDisplayText(user),
					classroom: classroomDisplayName,
				}),
				description: t("errors.unexpectedError"),
			});
		} finally {
			setIsSaving(false);
		}
	};

	const handleRemoveTeacher = async (selection: UserInfo | string): Promise<boolean> => {
		if (teachers.length <= 1) {
			setTeacherError(t("overview.settingsMinTeachersRequired"));
			return false;
		}

		const teacherToRemove = findTeacherBySelection(selection);
		if (!teacherToRemove) {
			return false;
		}

		setIsSaving(true);
		try {
			const foundUser = await getUserDetail(teacherToRemove.username, false, false, true);
			if (!foundUser) {
				errorToast({
					title: t("overview.settingsTeacherRemoveError", {
						user: userDisplayText(teacherToRemove),
						classroom: classroomDisplayName,
					}),
					description: t("errors.unexpectedError"),
				});
				return false;
			}

			await removeTeacherMutation.mutateAsync(foundUser.qoriaUserId);

			successToast({
				isClosable: true,
				title: t("overview.settingsTeacherRemovedSuccess", {
					user: userDisplayText(foundUser),
					classroom: classroomDisplayName,
				}),
			});
			return true;
		} catch (error) {
			console.error("Failed to remove teacher from class:", error);
			errorToast({
				title: t("overview.settingsTeacherRemoveError", {
					user: userDisplayText(teacherToRemove),
					classroom: classroomDisplayName,
				}),
				description: t("errors.unexpectedError"),
			});
			return false;
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<Box>
			<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px" mb={4}>
				{isGroup ? t("classroomEdit.groupTeachersDescription") : t("classroomEdit.teachersDescription")}
			</Box>

			{isClassroomSyncedStatus && !isGroup ? (
				<Box className="ClassroomEdit_modalDescriptionText--warning" mb={4}>
					{t("classroomEdit.syncOverrideWarning")}
				</Box>
			) : null}

			{lastSelectedTeacher?.isTeacher === false ? (
				<Box className="ClassroomEdit_validationError" mb={2}>
					<ChakraFAIcon icon="fa-minus-circle" variant="solid" />
					&nbsp;
					{t("classroomEdit.notTeacherGroup", { username: lastSelectedTeacher.username })}&nbsp;
				</Box>
			) : null}

			{teacherError ? (
				<Box className="ClassroomEdit_validationError" mb={2}>
					<ChakraFAIcon icon="fa-minus-circle" variant="solid" />
					&nbsp;
					{teacherError}
				</Box>
			) : null}

			<NuMultiSelect
				allItemsCb={getUsersCb}
				selectedItems={teachers.map((user) => getUserDisplayTextForSelect(user as UserInfo))}
				searchPlaceholder={t("classroomEdit.teacherSearchPlaceholder")}
				selectedPlaceholder={t("classroomEdit.noTeachersSelected")}
				onAdd={handleAddTeacher}
				onRemove={handleRemoveTeacher}
				onChange={() => setTeacherError(undefined)}
				title={t("classroomEdit.teachersTitle")}
				dataCyInput="teachers-classroom_selection"
				dataCyItemsContainer="teachers-classroom_container"
				minChars={3}
				disabled={!isTeachersLoaded || isSaving || isGroup}
				disableRemove={teachers.length <= 1}
				dataTestId="teacher-search-input"
			/>

			{isSaving || !isTeachersLoaded ? (
				<Box display="flex" justifyContent="center" py={8}>
					<Spinner />
				</Box>
			) : null}
		</Box>
	);
};
